import { useEffect, useRef } from 'react';
import { VideoPresets } from 'livekit-client';
import { toast } from 'react-toastify';
import { convertStatsReport } from 'vcs-common';
import { createAudioHealthLogger } from '../../audioHealthLog';
export function useLiveKitRtcStats({ room, connected, roomCode, localAudioTrackRef, localVideoTrackRef, audioOutputDeviceIdRef, statsTimerRef, onStatsReport, onMicrophoneUnheardChange, }) {
    const audioHealthLoggerRef = useRef(createAudioHealthLogger(onMicrophoneUnheardChange));
    const cpuLimitedSamplesRef = useRef(0);
    const cpuFallbackAppliedRef = useRef(false);
    useEffect(() => {
        if (!connected) {
            cpuLimitedSamplesRef.current = 0;
            cpuFallbackAppliedRef.current = false;
        }
    }, [connected]);
    useEffect(() => {
        let localIntervalId = undefined;
        let cancelled = false;
        if (!connected) {
            return undefined;
        }
        const id = setInterval(() => {
            console.debug(`Collecting stats task started local participant ${room.localParticipant.identity}-${room.localParticipant.sid}
          Local audio track info: mediaStreamTrack.id ${localAudioTrackRef.current?.mediaStreamTrack.id}, sid ${localAudioTrackRef.current?.sid}
          Local video track info: mediaStreamTrack.id ${localVideoTrackRef.current?.mediaStreamTrack.id}, sid ${localVideoTrackRef.current?.sid}`);
            // приходится брать из приватного поля, т.к. иначе не получить полную статистику, включающую дата канал
            const publisherPromise = room.engine.pcManager?.publisher?._pc?.getStats().catch((error) => {
                console.error('Failed to get publisher rtc report', error);
            });
            const subscriberPromise = room.engine.pcManager?.subscriber?._pc?.getStats().catch((error) => {
                console.error('Failed to get subscriber rtc report', error);
            });
            Promise.allSettled([publisherPromise, subscriberPromise]).then(async ([publisherResult, subscriberResult]) => {
                if (cancelled)
                    return;
                let publisherReport = null;
                let subscriberReport = null;
                if (publisherResult.status === 'fulfilled') {
                    const rawPublisherReport = publisherResult.value;
                    publisherReport = convertStatsReport(rawPublisherReport);
                    // CPU fallback из release/2.0.0: снижаем разрешение и частоту кадров.
                    let cpuLimited = false;
                    rawPublisherReport?.forEach((stat) => {
                        if (stat.type === 'outbound-rtp' &&
                            (stat.kind === 'video' || stat.mediaType === 'video') &&
                            stat.qualityLimitationReason === 'cpu') {
                            cpuLimited = true;
                        }
                    });
                    cpuLimitedSamplesRef.current = cpuLimited ? cpuLimitedSamplesRef.current + 1 : 0;
                    if (cpuLimitedSamplesRef.current >= 2 && !cpuFallbackAppliedRef.current) {
                        cpuFallbackAppliedRef.current = true;
                        /**
                         * Размытие и виртуальный фон намеренно не трогаем: выбор фона
                         * принадлежит пользователю, автоматически его снимать нельзя.
                         * Под нагрузкой отступаем только по разрешению и частоте кадров.
                         */
                        if (localVideoTrackRef.current) {
                            try {
                                await localVideoTrackRef.current.restartTrack({
                                    resolution: VideoPresets.h540.resolution,
                                    frameRate: 24,
                                });
                                toast.warning('Качество видео снижено: устройство перегружено. Аудио продолжает работать.');
                            }
                            catch (error) {
                                console.warn('Failed to reduce capture resolution after CPU limitation', error);
                            }
                        }
                    }
                }
                else {
                    console.error('Failed to get publisher rtc report', publisherResult.reason);
                }
                if (subscriberResult.status === 'fulfilled') {
                    subscriberReport = convertStatsReport(subscriberResult.value);
                }
                else {
                    console.error('Failed to get subscriber rtc report', subscriberResult.reason);
                }
                // Реальные счётчики WebRTC — единственный источник правды о том, идёт ли звук
                audioHealthLoggerRef.current({
                    publisherReport: publisherResult.status === 'fulfilled' ? publisherResult.value : undefined,
                    subscriberReport: subscriberResult.status === 'fulfilled' ? subscriberResult.value : undefined,
                    room,
                    isMicrophoneMuted: localAudioTrackRef.current?.isMuted ?? false,
                    hasMicrophone: !!localAudioTrackRef.current,
                });
                const statDto = {
                    roomCode: roomCode,
                    localUserIdentity: room.localParticipant.identity,
                    serverType: 'livekit',
                    hasCamera: !!localVideoTrackRef.current,
                    isCameraMuted: localVideoTrackRef.current?.isMuted ?? null,
                    videoTrackId: localVideoTrackRef.current?.mediaStreamTrack?.id ?? null,
                    hasMicrophone: !!localAudioTrackRef.current,
                    isMicrophoneMuted: localAudioTrackRef.current?.isMuted ?? null,
                    audioTrackId: localAudioTrackRef.current?.mediaStreamTrack?.id ?? null,
                    hasSpeakers: !!audioOutputDeviceIdRef.current,
                    userAgent: navigator.userAgent,
                    // eslint-disable-next-line no-restricted-globals
                    screenH: screen.height,
                    // eslint-disable-next-line no-restricted-globals
                    screenW: screen.width,
                    publisherStats: publisherReport,
                    subscriberStats: subscriberReport,
                };
                onStatsReport(statDto);
            });
        }, 5000);
        localIntervalId = id;
        statsTimerRef.current = id;
        console.debug(`Started timer ${statsTimerRef.current}`);
        return () => {
            cancelled = true;
            if (localIntervalId) {
                console.debug(`Clearing timer ${statsTimerRef.current}`);
                clearInterval(statsTimerRef.current);
            }
        };
    }, [
        audioOutputDeviceIdRef,
        connected,
        localAudioTrackRef,
        localVideoTrackRef,
        onStatsReport,
        room,
        roomCode,
        statsTimerRef,
    ]);
}
